/**
 * Wayfinding — pure projection and selection logic for the minimap and the
 * building callout. Kept free of React and PlayCanvas so the coordinate maths
 * can be unit-tested in isolation.
 */

import {
  DISTRICT_MIN_X, DISTRICT_MAX_X, DISTRICT_MIN_Z, DISTRICT_MAX_Z,
  PLOTS, plotFootprint, plotDoorPosition,
  type PlotDef,
} from '../pc/world/district'

// ── District geometry as a wayfinding target set ───────────────────────────

/** One labelled building on the map. */
export interface WayBuilding {
  id: string
  name: string
  domain: string
  /** Footprint centre — where the dot sits on the minimap. */
  cx: number
  cz: number
  /** Street door — where the player actually approaches; used for callouts. */
  doorX: number
  doorZ: number
  /** One-line "what's inside", derived from the portal domain. */
  blurb: string
}

/** What each portal domain means, in one plain line for the callout. */
export const DOMAIN_BLURB: Readonly<Record<string, string>> = {
  board:     'Governance & direction',
  ops:       'Operations & the daily run',
  hq:        'The working office floor',
  metrics:   'Live metrics & cost',
  workflows: 'Workflow benches & pipelines',
  chat:      'Conversations over coffee',
  knowledge: 'Knowledge, docs & memory',
  servers:   'Servers & compute',
}

/**
 * A short, stable colour per domain so a dot reads the same on the minimap as
 * the building's own tint intent. Muted to sit inside the cream/indigo HUD.
 */
export const DOMAIN_COLOR: Readonly<Record<string, string>> = {
  board:     '#c8cbe0',
  ops:       '#e0b487',
  hq:        '#f3e8c8',
  metrics:   '#8fd3e0',
  workflows: '#e0b487',
  chat:      '#e8a86a',
  knowledge: '#d8c98a',
  servers:   '#8f97a4',
}

function toBuilding(plot: PlotDef): WayBuilding {
  const f = plotFootprint(plot)
  const [doorX, doorZ] = plotDoorPosition(plot)
  const domain = plot.domain ?? ''
  return {
    id: plot.id,
    name: plot.name,
    domain,
    cx: (f.minX + f.maxX) / 2,
    cz: (f.minZ + f.maxZ) / 2,
    doorX,
    doorZ,
    blurb: DOMAIN_BLURB[domain] ?? '',
  }
}

/** The eight named, enterable buildings — the wayfinding targets. */
export const WAY_BUILDINGS: readonly WayBuilding[] = PLOTS
  .filter((p) => p.enterable && p.name.length > 0)
  .map(toBuilding)

// ── World → minimap projection ─────────────────────────────────────────────

export interface MinimapBox {
  /** Drawable width/height of the minimap in px. */
  width: number
  height: number
  /** Inset so dots/arrow near the edge aren't clipped. */
  pad: number
}

/** Normalised 0..1 position within the district (x east, y = north→south). */
export function normalizeToDistrict(worldX: number, worldZ: number): { nx: number; ny: number } {
  const nx = (worldX - DISTRICT_MIN_X) / (DISTRICT_MAX_X - DISTRICT_MIN_X)
  const ny = (worldZ - DISTRICT_MIN_Z) / (DISTRICT_MAX_Z - DISTRICT_MIN_Z)
  return { nx, ny }
}

/**
 * Project a world XZ point to minimap pixel coordinates. North (-Z) is up,
 * east (+X) is right. Clamped into the padded box so an off-map position (e.g.
 * the interior island) parks at the edge rather than flying off.
 */
export function projectToMinimap(worldX: number, worldZ: number, box: MinimapBox): { x: number; y: number } {
  const { nx, ny } = normalizeToDistrict(worldX, worldZ)
  const innerW = box.width - box.pad * 2
  const innerH = box.height - box.pad * 2
  const x = box.pad + clamp01(nx) * innerW
  const y = box.pad + clamp01(ny) * innerH
  return { x, y }
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v
}

// ── Facing / callout selection ─────────────────────────────────────────────

/** Unit world facing vector for a yaw (rad): 0 = north (-Z), clockwise. */
export function facingVector(yawRad: number): { x: number; z: number } {
  return { x: Math.sin(yawRad), z: -Math.cos(yawRad) }
}

/** Within this many metres of a door, proximity alone surfaces the callout. */
export const CALLOUT_NEAR_RADIUS = 11
/** Beyond the near radius, a building must also be looked at, up to this range. */
export const CALLOUT_GAZE_RADIUS = 55
/** Half-angle of the gaze cone, in radians (~28°). */
export const CALLOUT_GAZE_CONE = 0.49

export interface CalloutPick {
  building: WayBuilding
  distance: number
  /** Angular deviation from straight-ahead, in radians. */
  angle: number
  /** True when selected by proximity rather than gaze. */
  near: boolean
}

/**
 * Choose the single building to call out, or null. Proximity wins: if the
 * player stands within CALLOUT_NEAR_RADIUS of any door, the nearest such
 * building is shown. Otherwise the most directly looked-at building inside the
 * gaze cone and range is shown (tie-broken by distance).
 */
export function selectCallout(
  px: number,
  pz: number,
  yawRad: number,
  buildings: readonly WayBuilding[] = WAY_BUILDINGS,
): CalloutPick | null {
  const facing = facingVector(yawRad)

  let nearBest: CalloutPick | null = null
  let gazeBest: CalloutPick | null = null

  for (const b of buildings) {
    const dx = b.doorX - px
    const dz = b.doorZ - pz
    const distance = Math.hypot(dx, dz)
    if (distance < 1e-3) {
      // Standing on the door — treat as maximally near, straight ahead.
      const pick: CalloutPick = { building: b, distance, angle: 0, near: true }
      if (!nearBest || distance < nearBest.distance) nearBest = pick
      continue
    }

    if (distance <= CALLOUT_NEAR_RADIUS) {
      if (!nearBest || distance < nearBest.distance) {
        nearBest = { building: b, distance, angle: 0, near: true }
      }
      continue
    }

    if (distance <= CALLOUT_GAZE_RADIUS) {
      const invLen = 1 / distance
      const dot = facing.x * dx * invLen + facing.z * dz * invLen
      const angle = Math.acos(Math.max(-1, Math.min(1, dot)))
      if (angle <= CALLOUT_GAZE_CONE) {
        if (
          !gazeBest ||
          angle < gazeBest.angle - 1e-6 ||
          (Math.abs(angle - gazeBest.angle) <= 1e-6 && distance < gazeBest.distance)
        ) {
          gazeBest = { building: b, distance, angle, near: false }
        }
      }
    }
  }

  return nearBest ?? gazeBest
}
